/**
 * Runtime configuration lookup, free of side effects.
 *
 * Reads settings from window.ENV (written by env.sh into env.js), accepting
 * both REACT_APP_-prefixed and bare keys, and falls back to the value baked in
 * at build time.
 *
 * This lives apart from ./env because that module validates the API URL as it
 * loads and throws when one is missing. A component reading a single feature
 * flag should not have to satisfy that check.
 */

/** Parse the "true"/"false" strings env.sh writes. */
export const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';
  return false;
};

/**
 * Read a single runtime-configurable setting, preferring env.js over the value
 * baked in at build time.
 *
 * @param {string} name setting name without the REACT_APP_ prefix
 * @param {string} [fallback] used when neither runtime nor build time supplies one
 */
export const getRuntimeSetting = (name, fallback = undefined) => {
  const prefixed = `REACT_APP_${name}`;
  // window.ENV is absent in local dev, where /env.js is never generated.
  const runtime = (typeof window !== 'undefined' && window.ENV) || {};
  return runtime[prefixed] ?? runtime[name] ?? process.env[prefixed] ?? fallback;
};

/** The same lookup, parsed as a boolean. */
export const getRuntimeFlag = (name, fallback = 'true') =>
  parseBoolean(getRuntimeSetting(name, fallback));
